import { useEffect, useState } from "react";
import { Button, Card, Checkbox, Stack, Switch, Text } from "@mantine/core";
import type { CreateReviewInvitationRequest, ReviewInvitationPreviewItem } from "@/models/club";
import { useReviewCapabilities } from "@/services/reviews";
import { blockerLabel, hasReviewAction, reviewError, type ReviewApiError } from "@/services/reviews/policy";

type Customer = { id?: number; phoneNumber?: string | null };
export function useInvoiceReview(customer: Customer | null, items: ReviewInvitationPreviewItem[], submitting: boolean) {
  const capability = useReviewCapabilities();
  const caps = capability.data;
  const key = JSON.stringify([customer?.id, customer?.phoneNumber, items]);
  const [selection, setSelection] = useState({ key: "", requested: false });
  useEffect(() => { setSelection({ key, requested: false }); }, [key]);
  const selected = selection.key === key && selection.requested;
  const permitted = !!caps?.enabled && !!caps.siteAvailable && hasReviewAction(caps.allowedActions, "reviews.invite");
  const validItems = items.length > 0 && items.every(item => !!item.merchantServiceId && !!item.merchantUserId);
  const eligible = permitted && !!customer?.id && validItems;
  const denied = (capability.error as ReviewApiError | null)?.response?.status === 403 || caps?.disabledReason === "REVIEW_PERMISSION_DENIED";
  const unavailableReason = capability.isLoading ? "در حال بررسی امکان ارسال…"
    : capability.isError ? reviewError(capability.error)
    : caps?.disabledReason ? blockerLabel(caps.disabledReason)
    : !permitted ? "ارسال نظرسنجی برای این حساب یا سالن فعال نیست."
    : !customer?.id ? "ابتدا مشتری فاکتور را انتخاب کنید."
    : items.length === 0 ? "ابتدا خدمت انجام‌شده را به فاکتور اضافه کنید."
    : !validItems ? "برای همه‌ی خدمت‌ها انجام‌دهنده را انتخاب کنید." : "";
  const payload: CreateReviewInvitationRequest | undefined = selected && eligible ? {
    requested: true,
    // Checking the invoice invitation option is the single confirmation step.
    servicesPerformedConfirmed: true,
    templateVersion: caps?.templateVersion,
  } : undefined;
  const mobileControl = denied ? null : (
    <Stack gap={5}>
      <Checkbox label="ارسال پیامک نظرسنجی پس از ثبت فاکتور" checked={selected}
        disabled={submitting || (!selected && (!eligible || capability.isFetching))}
        onChange={event => setSelection({ key, requested: event.currentTarget.checked })} />
      {unavailableReason && <Text size="xs" c="dimmed">{unavailableReason}</Text>}
      {(capability.isError || (!!caps && !permitted)) && <Button size="compact-xs" variant="subtle"
        loading={capability.isFetching} disabled={submitting} onClick={() => void capability.refetch()}>بررسی دوباره امکان ارسال</Button>}
    </Stack>
  );
  const control = denied ? null : <Card withBorder radius="md" p="sm">{mobileControl}</Card>;
  // A settings-style row for the desktop invoice summary: one switch, what it does, and why it is off.
  const row = denied ? null : (
    <Stack gap={4}>
      <Switch color="violet" checked={selected}
        disabled={submitting || (!selected && (!eligible || capability.isFetching))}
        onChange={event => setSelection({ key, requested: event.currentTarget.checked })}
        label="پیامک نظرسنجی"
        description={unavailableReason || "بعد از ثبت فاکتور، نظر مشتری درباره‌ی خدمت پرسیده می‌شود."}
        styles={{ body: { justifyContent: "space-between" }, label: { fontWeight: 600 }, labelWrapper: { flex: 1 } }}
        labelPosition="left" />
      {(capability.isError || (!!caps && !permitted)) && <Button size="compact-xs" variant="subtle" w="fit-content"
        loading={capability.isFetching} disabled={submitting} onClick={() => void capability.refetch()}>بررسی دوباره امکان ارسال</Button>}
    </Stack>
  );
  return { control, mobileControl, row, payload, blocked: selected && !eligible, reset: () => setSelection({ key: "", requested: false }) };
}
